import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, LOGO_URL } from "@/constants";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import SmartImage from "@/components/SmartImage";
import { Plus, Minus, Trash2, LogOut, ShoppingBag, Store, Package, User, CheckCircle2, FileDown } from "lucide-react";

const DELIVERY_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const inputCls =
  "w-full rounded-lg border border-kera-line bg-white px-3.5 py-2 text-sm text-kera-ink outline-none focus:border-kera-gold";

const STATUS_STYLE = {
  pending: "bg-kera-line/60 text-kera-muted",
  confirmed: "bg-kera-gold/20 text-kera-copper",
  packed: "bg-kera-gold/20 text-kera-copper",
  shipped: "bg-black text-kera-gold",
  delivered: "bg-black text-white",
  cancelled: "bg-red-100 text-red-700",
};

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted mb-1.5">{label}</label>
      {children}
    </div>
  );
}

export default function TraderDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("order");
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [branches, setBranches] = useState([]);
  const [orders, setOrders] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [note, setNote] = useState("");
  const [deliveryDay, setDeliveryDay] = useState("");
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [branchForm, setBranchForm] = useState(null);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });

  const authed = { withCredentials: true };
  const isTrader = user && user.role === "trader";
  const approved = isTrader && user.status === "approved";

  const loadProducts = () => axios.get(`${API}/trade/products`, authed).then((r) => setProducts(r.data.products)).catch(() => {});
  const loadBranches = () => axios.get(`${API}/trade/branches`, authed).then((r) => setBranches(r.data.branches)).catch(() => {});
  const loadOrders = () => axios.get(`${API}/trade/orders`, authed).then((r) => setOrders(r.data.orders)).catch(() => {});

  useEffect(() => {
    if (user === false) navigate("/trade", { replace: true });
    else if (user && user.role !== "trader") navigate("/admin", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (approved) {
      loadProducts();
      loadBranches();
      loadOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approved]);

  const lines = useMemo(
    () =>
      Object.entries(cart)
        .map(([pid, qty]) => {
          const p = products.find((x) => x.id === pid);
          if (!p || qty < 1) return null;
          const price = parseFloat(String(p.price || "0").replace("€", "")) || 0;
          return { ...p, qty, price, line: price * qty };
        })
        .filter(Boolean),
    [cart, products]
  );
  const total = lines.reduce((s, l) => s + l.line, 0);

  const setQty = (pid, qty) =>
    setCart((c) => {
      const next = { ...c };
      if (qty <= 0) delete next[pid];
      else next[pid] = qty;
      return next;
    });

  const placeOrder = async () => {
    if (!branchId) {
      toast.error("Select a delivery branch");
      return;
    }
    if (!lines.length) {
      toast.error("Add at least one product");
      return;
    }
    setPlacing(true);
    try {
      const r = await axios.post(`${API}/trade/orders`, {
        branch_id: branchId,
        note,
        delivery_day: deliveryDay,
        items: lines.map((l) => ({ product_id: l.id, qty: l.qty })),
      }, authed);
      setPlaced(r.data.order_no);
      setCart({});
      setNote("");
      setDeliveryDay("");
      loadOrders();
      loadProducts();
      toast.success(`Order ${r.data.order_no} placed`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPlacing(false);
    }
  };

  const saveBranch = async (e) => {
    e.preventDefault();
    try {
      if (branchForm.id) {
        await axios.put(`${API}/trade/branches/${branchForm.id}`, branchForm, authed);
        toast.success("Branch updated");
      } else {
        await axios.post(`${API}/trade/branches`, branchForm, authed);
        toast.success("Branch added");
      }
      setBranchForm(null);
      loadBranches();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteBranch = async (b) => {
    if (!window.confirm(`Remove branch "${b.name}"?`)) return;
    try {
      await axios.delete(`${API}/trade/branches/${b.id}`, authed);
      loadBranches();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadInvoice = async (o) => {
    try {
      const r = await axios.get(`${API}/trade/orders/${o.id}/invoice`, { ...authed, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice-${o.order_no}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download invoice");
    }
  };

  const changePassword = async (e) => {    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      }, authed);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!user || !isTrader) return null;

  if (!approved) {
    return (
      <main className="min-h-screen bg-black text-white font-body flex items-center justify-center px-4" data-testid="trader-pending">
        <div className="max-w-md text-center">
          <img src={LOGO_URL} alt="KERA" className="h-12 w-auto mx-auto mb-8" />
          <h1 className="font-display font-bold text-3xl">Application under review</h1>
          <p className="mt-4 text-sm text-white/60 leading-relaxed">
            Thank you, {user.name}. Our team is reviewing your trade account. You will receive an email at{" "}
            <span className="text-kera-gold">{user.email}</span> once approved — then wholesale pricing and ordering unlock here.
          </p>
          <button onClick={logout} className="mt-8 rounded-full border border-white/25 px-6 py-2.5 text-sm hover:border-kera-gold hover:text-kera-gold transition-colors" data-testid="trader-pending-logout">
            Sign out
          </button>
        </div>
      </main>
    );
  }

  const TABS = [
    ["order", "Order Products", Package],
    ["orders", "My Orders", ShoppingBag],
    ["branches", "Branches", Store],
    ["account", "Account", User],
  ];

  return (
    <main className="min-h-screen bg-[#FAF7F0] text-kera-ink font-body" data-testid="trader-dashboard">
      <header className="border-b border-kera-line bg-black sticky top-0 z-40">
        <div className="px-4 sm:px-8 max-w-6xl mx-auto h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/"><img src={LOGO_URL} alt="KERA" className="h-8 w-auto" /></Link>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-kera-gold">Trader Portal</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-white/60" data-testid="trader-user-email">{user.company || user.name} · {user.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-white/60 hover:text-kera-gold transition-colors" data-testid="trader-logout">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="px-4 sm:px-8 max-w-6xl mx-auto py-10">
        <div className="flex gap-2 mb-8 overflow-x-auto pb-1" data-testid="trader-tabs">
          {TABS.map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`trader-tab-${key}`}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-5 py-2 text-xs font-mono uppercase tracking-[0.15em] border transition-colors ${
                tab === key ? "bg-black text-white border-black" : "border-kera-line text-kera-muted hover:border-kera-copper bg-white"
              }`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {tab === "order" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="trader-order-panel">
            <div className="lg:col-span-2 bg-white border border-kera-line rounded-2xl overflow-hidden">
              <table className="w-full text-sm" data-testid="trade-products-table">
                <thead>
                  <tr className="border-b border-kera-line text-left">
                    {["", "Product", "Pack", "Price", "Stock", "Qty"].map((h) => (
                      <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => {
                    const stock = p.stock_qty ?? null;
                    const out = stock !== null && stock <= 0;
                    const atMax = stock !== null && (cart[p.id] || 0) >= stock;
                    return (
                    <tr key={p.id} className={`border-b border-kera-line/60 last:border-0 ${out ? "opacity-50" : ""}`} data-testid={`trade-product-${p.id}`}>
                      <td className="px-4 py-3">{p.image && <SmartImage src={p.image} alt="" sizes="40px" className="h-10 w-10 rounded-lg object-cover border border-kera-line" />}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-kera-ink">{p.name}</p>
                        <p className="text-xs text-kera-muted">{p.category}</p>
                      </td>
                      <td className="px-4 py-3 text-kera-muted text-xs">{p.net_weight || "—"}</td>
                      <td className="px-4 py-3 font-semibold text-kera-copper" data-testid={`trade-price-${p.id}`}>{p.price ? `€${p.price}` : "—"}</td>
                      <td className="px-4 py-3" data-testid={`trade-stock-${p.id}`}>
                        {stock === null ? (
                          <span className="text-xs text-kera-muted">—</span>
                        ) : out ? (
                          <span className="rounded-full bg-red-100 text-red-700 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.12em]">Out of stock</span>
                        ) : (
                          <span className={`text-xs font-mono ${stock <= 10 ? "text-kera-copper font-semibold" : "text-kera-muted"}`}>{stock} left</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="inline-flex items-center gap-2 border border-kera-line rounded-full px-1.5 py-1">
                          <button onClick={() => setQty(p.id, (cart[p.id] || 0) - 1)} disabled={out} className="p-1 text-kera-muted hover:text-kera-copper disabled:opacity-30" data-testid={`qty-minus-${p.id}`}><Minus size={12} /></button>
                          <span className="w-6 text-center font-mono text-sm" data-testid={`qty-value-${p.id}`}>{cart[p.id] || 0}</span>
                          <button onClick={() => setQty(p.id, (cart[p.id] || 0) + 1)} disabled={out || atMax} className="p-1 text-kera-muted hover:text-kera-copper disabled:opacity-30" data-testid={`qty-plus-${p.id}`}><Plus size={12} /></button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-kera-line rounded-2xl p-6 h-fit sticky top-24" data-testid="order-summary">
              <h3 className="font-display font-semibold text-lg text-kera-ink">Order Summary</h3>
              {placed && (
                <div className="mt-4 rounded-xl bg-kera-gold/15 border border-kera-gold/50 p-4 text-sm text-kera-ink" data-testid="order-placed-note">
                  <CheckCircle2 size={16} className="inline mr-2 text-kera-copper" />
                  Order <strong>{placed}</strong> placed — confirmation sent by email.
                </div>
              )}
              <div className="mt-4 space-y-2 max-h-56 overflow-y-auto">
                {lines.length === 0 && <p className="text-sm text-kera-muted">No items yet — set quantities on the left.</p>}
                {lines.map((l) => (
                  <div key={l.id} className="flex justify-between items-center text-sm" data-testid={`cart-line-${l.id}`}>
                    <span className="text-kera-ink">{l.name} × {l.qty}</span>
                    <span className="font-medium text-kera-ink">€{l.line.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-kera-line flex justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted">Total</span>
                <span className="font-display font-bold text-xl text-kera-ink" data-testid="order-total">€{total.toFixed(2)}</span>
              </div>
              <div className="mt-5 space-y-4">
                <Field label="Deliver to branch">
                  <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={inputCls} data-testid="order-branch-select">
                    <option value="">Select branch…</option>
                    {branches.filter((b) => b.active).map((b) => (
                      <option key={b.id} value={b.id}>{b.name} — {b.postcode}</option>
                    ))}
                  </select>
                </Field>
                {branches.length === 0 && (
                  <p className="text-xs text-kera-muted">No branches yet — add one in the Branches tab first.</p>
                )}
                <Field label="Preferred delivery day">
                  <select value={deliveryDay} onChange={(e) => setDeliveryDay(e.target.value)} className={inputCls} data-testid="order-delivery-day">
                    <option value="">No preference</option>
                    {DELIVERY_DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Order note">
                  <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} data-testid="order-note" />
                </Field>
                <button
                  onClick={placeOrder}
                  disabled={placing || !lines.length}
                  className="w-full rounded-full bg-kera-gold text-black py-3 text-sm font-semibold hover:bg-black hover:text-kera-gold transition-colors disabled:opacity-50"
                  data-testid="place-order-button"
                >
                  {placing ? "Placing order…" : "Place Order"}
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="trader-orders-panel">
            {orders.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-kera-line rounded-2xl bg-white">
                <p className="font-display font-semibold text-xl text-kera-ink">No orders yet</p>
                <p className="mt-2 text-sm text-kera-muted">Your orders and their live status will appear here.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} className="bg-white border border-kera-line rounded-2xl p-6" data-testid={`order-card-${o.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-display font-semibold text-lg text-kera-ink">{o.order_no}</p>
                        <p className="text-xs text-kera-muted mt-1">
                          {o.created_at ? new Date(o.created_at).toLocaleString() : ""} · Deliver to {o.branch_name}
                          {o.delivery_day ? ` · Preferred day: ${o.delivery_day}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <button onClick={() => downloadInvoice(o)} className="inline-flex items-center gap-1.5 rounded-full border border-kera-line px-4 py-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-kera-muted hover:border-kera-copper hover:text-kera-copper transition-colors" data-testid={`order-invoice-${o.id}`}>
                          <FileDown size={12} /> Invoice
                        </button>
                        <span className={`rounded-full px-4 py-1.5 text-[10px] font-mono uppercase tracking-[0.15em] ${STATUS_STYLE[o.status] || STATUS_STYLE.pending}`} data-testid={`order-status-${o.id}`}>
                          {o.status}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {o.items.map((i, idx) => (
                        <span key={idx} className="rounded-full bg-[#FAF7F0] border border-kera-line px-3 py-1 text-xs text-kera-ink">
                          {i.name} × {i.qty}
                        </span>
                      ))}
                    </div>
                    <p className="mt-3 text-sm font-semibold text-kera-ink">Total: €{o.total?.toFixed(2)}</p>
                    <div className="mt-5 border-t border-kera-line/70 pt-4">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted mb-3">Status History</p>
                      <ol className="space-y-2">
                        {(o.status_history || []).map((h, i) => (
                          <li key={i} className="flex items-center gap-3 text-xs text-kera-muted">
                            <span className="h-2 w-2 rounded-full bg-kera-gold shrink-0" />
                            <span className="capitalize font-medium text-kera-ink">{h.status}</span>
                            {h.note && <span>— {h.note}</span>}
                            <span className="ml-auto">{h.at ? new Date(h.at).toLocaleString() : ""}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "branches" && (
          <div className="max-w-2xl" data-testid="trader-branches-panel">
            {branchForm ? (
              <form onSubmit={saveBranch} className="bg-white border border-kera-line rounded-2xl p-6 sm:p-8" data-testid="branch-form">
                <h3 className="font-display font-semibold text-xl text-kera-ink mb-6">{branchForm.id ? "Edit Branch" : "Add Branch"}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Branch / Shop Name">
                    <input required value={branchForm.name} onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })} className={inputCls} data-testid="branch-field-name" />
                  </Field>
                  <Field label="Country">
                    <input value={branchForm.country} onChange={(e) => setBranchForm({ ...branchForm, country: e.target.value })} className={inputCls} data-testid="branch-field-country" />
                  </Field>
                  <Field label="Address">
                    <input value={branchForm.address} onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })} className={inputCls} data-testid="branch-field-address" />
                  </Field>
                  <Field label="Postcode">
                    <input value={branchForm.postcode} onChange={(e) => setBranchForm({ ...branchForm, postcode: e.target.value })} className={inputCls} data-testid="branch-field-postcode" />
                  </Field>
                </div>
                <div className="mt-6 flex gap-3">
                  <button type="submit" className="rounded-full bg-black text-white px-6 py-2.5 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="branch-save">Save Branch</button>
                  <button type="button" onClick={() => setBranchForm(null)} className="rounded-full border border-kera-line px-6 py-2.5 text-sm text-kera-muted" data-testid="branch-cancel">Cancel</button>
                </div>
              </form>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-kera-muted">{branches.length} branch{branches.length === 1 ? "" : "es"} — orders are delivered to the branch you select at checkout.</p>
                  <button onClick={() => setBranchForm({ name: "", address: "", postcode: "", country: "", active: true })} className="inline-flex items-center gap-2 rounded-full bg-black text-white px-5 py-2.5 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="add-branch-button">
                    <Plus size={15} /> Add Branch
                  </button>
                </div>
                <div className="space-y-3">
                  {branches.map((b) => (
                    <div key={b.id} className="bg-white border border-kera-line rounded-2xl px-5 py-4 flex items-center gap-4" data-testid={`branch-row-${b.id}`}>
                      <Store size={16} className="text-kera-copper shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-kera-ink">{b.name}</p>
                        <p className="text-xs text-kera-muted truncate">{[b.address, b.postcode, b.country].filter(Boolean).join(", ")}</p>
                      </div>
                      <button onClick={() => setBranchForm({ ...b })} className="text-xs font-mono uppercase tracking-[0.15em] text-kera-muted hover:text-kera-copper" data-testid={`branch-edit-${b.id}`}>Edit</button>
                      <button onClick={() => deleteBranch(b)} className="p-2 text-kera-muted hover:text-red-700" data-testid={`branch-delete-${b.id}`}><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "account" && (
          <div className="max-w-md" data-testid="trader-account-panel">
            <form onSubmit={changePassword} className="bg-white border border-kera-line rounded-2xl p-6 sm:p-8" data-testid="change-password-form">
              <h3 className="font-display font-semibold text-xl text-kera-ink mb-2">Change Password</h3>
              <p className="text-sm text-kera-muted mb-6">Use at least 8 characters.</p>
              <div className="space-y-4">
                <Field label="Current Password">
                  <input type="password" required value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className={inputCls} data-testid="pw-current" />
                </Field>
                <Field label="New Password">
                  <input type="password" required minLength={8} value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className={inputCls} data-testid="pw-new" />
                </Field>
                <Field label="Confirm New Password">
                  <input type="password" required value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className={inputCls} data-testid="pw-confirm" />
                </Field>
              </div>
              <button type="submit" className="mt-6 w-full rounded-full bg-black text-white py-3 text-sm font-semibold hover:bg-kera-gold hover:text-black transition-colors" data-testid="pw-submit">
                Update Password
              </button>
            </form>
          </div>
        )}
      </div>
    </main>
  );
}
